import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import TemplateBasicInfo from "../Components/TemplateBasicInfo";
import TemplateIntroAndChapters from "../Components/TemplateIntroAndChapters";
import TemplateItemsBuilder from "../Components/TemplateItemsBuilder";
import { saveInspectionTemplate, updateInspectionTemplate, updateTemplateItems, getInspectionTemplate, fetchTemplateItems, setInspectionTemplate } from "../Reducers/actions";

const TemplateBuilder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get step from URL, default to 1
  const stepFromUrl = parseInt(searchParams.get('step')) || 1;
  const [currentStep, setCurrentStep] = useState(stepFromUrl);
  const [templateData, setTemplateData] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [templateItems, setTemplateItems] = useState([]);

  // Sync step with URL
  useEffect(() => {
    const urlStep = parseInt(searchParams.get('step')) || 1;
    if (urlStep !== currentStep) {
      setCurrentStep(urlStep);
    }
  }, [searchParams]);

  // Update URL when step changes
  useEffect(() => {
    const urlStep = parseInt(searchParams.get('step')) || 1;
    if (currentStep !== urlStep) {
      setSearchParams({ step: currentStep });
    }
  }, [currentStep]);

  useEffect(() => {
    if (id) {
      // Edit mode - haal bestaande template op
      dispatch(getInspectionTemplate(id)).then((result) => {
        if (result) {
          if (result.chapters && result.chapters.length > 0) {
            setChapters(result.chapters);
          }
          // In edit mode, default to step 2 (intro & chapters) if no step is specified
          if (!searchParams.get('step')) {
            setCurrentStep(2);
            setSearchParams({ step: 2 });
          }
        }
      });
      dispatch(fetchTemplateItems(id));
    } else {
      // Nieuw sjabloon - reset Redux store zodat oude data niet getoond wordt
      dispatch(setInspectionTemplate(null));
    }
  }, [dispatch, id]);

  const goToStep = (step) => {
    setCurrentStep(step);
    setSearchParams({ step });
  };

  const handleBasicInfoSubmit = (values) => {
    // Filter alleen basis informatie velden om te voorkomen dat andere data overschreven wordt
    const basicInfo = {
      name: values.name,
      description: values.description,
      category: values.category,
      certificaat: values.certificaat,
      version: values.version,
      is_default: values.is_default === 'true' || values.is_default === true,
      is_active: values.is_active === 'true' || values.is_active === true,
    };

    if (id) {
      // Update bestaande template
      dispatch(updateInspectionTemplate(id, basicInfo)).then((result) => {
        if (result && result.result) {
          setTemplateData(result.result);
          goToStep(2);
        } else {
          alert("Er is een fout opgetreden bij het bijwerken van het sjabloon.");
        }
      }).catch((err) => {
        console.error("Error updating template:", err);
        alert("Er is een fout opgetreden bij het bijwerken van het sjabloon.");
      });
    } else {
      // Nieuwe template aanmaken
      dispatch(saveInspectionTemplate(basicInfo)).then((result) => {
        if (result && result.success) {
          setTemplateData(result.result);
          goToStep(2);
        } else {
          alert("Er is een fout opgetreden bij het aanmaken van het sjabloon.");
        }
      }).catch((err) => {
        console.error("Error creating template:", err);
        alert("Er is een fout opgetreden bij het aanmaken van het sjabloon.");
      });
    }
  };

  const handleIntroAndChaptersSubmit = (values) => {
    const templateId = id || templateData._id;

    dispatch(updateInspectionTemplate(templateId, values)).then((result) => {
      if (result && result.success !== false) {
        setTemplateData({ ...templateData, ...values });
        setChapters(values.chapters || []);
        goToStep(3);
      } else {
        alert("Er is een fout opgetreden bij het opslaan van de introductie en hoofdstukken.");
      }
    }).catch((err) => {
      console.error("Error updating intro/chapters:", err);
      alert("Er is een fout opgetreden bij het opslaan van de introductie en hoofdstukken.");
    });
  };

  const handleItemsSubmit = (items) => {
    const templateId = id || templateData._id;

    dispatch(updateTemplateItems(templateId, items)).then((result) => {
      if (result.success) {
        alert("Sjabloon succesvol opgeslagen!");
        navigate("/zwembadinspecties/templates");
      } else {
        alert("Er is een fout opgetreden bij het opslaan van de items.");
      }
    });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3>{id ? "Sjabloon Bewerken" : "Nieuw Sjabloon Maken"}</h3>

              {/* Progress Steps */}
              <div className="mt-3">
                <div className="progress" style={{ height: "30px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${(currentStep / 3) * 100}%` }}
                    aria-valuenow={currentStep}
                    aria-valuemin="1"
                    aria-valuemax="3"
                  >
                    Stap {currentStep} van 3
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <span
                    className={currentStep === 1 ? "font-weight-bold" : "text-muted"}
                    style={{ cursor: 'pointer' }}
                    onClick={() => goToStep(1)}
                  >
                    1. Basis Informatie
                  </span>
                  <span
                    className={currentStep === 2 ? "font-weight-bold" : "text-muted"}
                    style={{ cursor: currentStep >= 2 || id ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (currentStep >= 2 || id) goToStep(2);
                    }}
                  >
                    2. Introductie & Hoofdstukken
                  </span>
                  <span
                    className={currentStep === 3 ? "font-weight-bold" : "text-muted"}
                    style={{ cursor: currentStep >= 3 ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (currentStep >= 3) goToStep(3);
                    }}
                  >
                    3. Items per Hoofdstuk
                  </span>
                </div>
              </div>
            </div>

            <div className="card-body">
              {currentStep === 1 && (
                <TemplateBasicInfo
                  onSubmit={handleBasicInfoSubmit}
                  initialData={templateData}
                />
              )}

              {currentStep === 2 && (
                <TemplateIntroAndChapters
                  onSubmit={handleIntroAndChaptersSubmit}
                  onBack={handleBack}
                  initialData={templateData}
                />
              )}

              {currentStep === 3 && (
                <TemplateItemsBuilder
                  templateId={id || templateData?._id}
                  chapters={chapters}
                  onSubmit={handleItemsSubmit}
                  onBack={handleBack}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;
